<template>
  <div class="payment-block">
    <div class="payment-header">
      <div class="payment-header-left">
        <v-icon>mdi-credit-card-outline</v-icon>
        <span>Payment</span>
      </div>
      <strong v-if="showAmount" class="payment-amount-value payment-header-amount text-success">
        {{ formattedAmount }}
      </strong>
    </div>
    <p class="payment-help">
      Enter your card details to secure the booking.
    </p>
    <div
      v-if="resolvedPublishableKey"
      ref="stripeCardMount"
      class="stripe-card-mount"
    ></div>
    <div v-else class="payment-missing">
      Stripe publishable key is missing. Set
      <strong>VUE_APP_STRIPE_PUBLISHABLE_KEY</strong>.
    </div>
    <div v-if="stripeError" class="payment-error">
      {{ stripeError }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
  loadStripe,
  type Stripe,
  type StripeCardElement,
  type StripeElements,
  type StripeCardElementChangeEvent,
  type PaymentIntentConfirmParams,
  type PaymentIntentResult,
  type SetupIntentResult,
  type StripeCardElementOptions,
  type StripeConstructorOptions,
} from "@stripe/stripe-js";

export interface BillingDetails {
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
}

const props = withDefaults(
  defineProps<{
    publishableKey: string;
    stripeAccount?: string;
    amount?: number | null;
    currency?: string;
  }>(),
  {
    publishableKey: "",
    stripeAccount: "",
    amount: null,
    currency: "USD",
  },
);

const defaultPublishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || "";

const resolvedPublishableKey = computed(() => props.publishableKey || defaultPublishableKey);
const resolvedStripeAccount = computed(() => props.stripeAccount || "");

const showAmount = computed(() => typeof props.amount === "number" && Number.isFinite(props.amount));
const formattedAmount = computed(() => {
  const amount = props.amount;
  if (typeof amount !== "number" || !Number.isFinite(amount)) return "-";

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: props.currency || "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Fallback if currency code is invalid.
    return `$${amount.toFixed(2)}`;
  }
});

const stripeCardMount = ref<HTMLElement | null>(null);
const stripeError = ref("");
const stripeInstance = ref<Stripe | null>(null);
const stripeElements = ref<StripeElements | null>(null);
const stripeCardElement = ref<StripeCardElement | null>(null);
const stripeReady = ref(false);
const stripeMounted = ref(false);
const stripeElementReady = ref(false);
const cardComplete = ref(false);
const stripeInitPromise = ref<Promise<void> | null>(null);
const stripeMountPromise = ref<Promise<void> | null>(null);
const mountedStripeHost = ref<HTMLElement | null>(null);

function resetStripeState() {
  stripeMounted.value = false;
  stripeElementReady.value = false;
  stripeReady.value = false;
  stripeInitPromise.value = null;
  stripeMountPromise.value = null;
  stripeElements.value = null;
  stripeInstance.value = null;
  mountedStripeHost.value = null;
}

function destroyStripeCard() {
  if (stripeCardElement.value && stripeMounted.value) {
    stripeCardElement.value.unmount();
  }

  if (stripeCardElement.value) {
    stripeCardElement.value.destroy();
    stripeCardElement.value = null;
  }

  resetStripeState();
}

async function initStripeCard() {
  if (!resolvedPublishableKey.value || stripeReady.value) return;
  if (stripeInitPromise.value) {
    await stripeInitPromise.value;
    return;
  }

  stripeInitPromise.value = (async () => {
    stripeError.value = "";
    try {
      const loadOptions: StripeConstructorOptions | undefined = resolvedStripeAccount.value
        ? { stripeAccount: resolvedStripeAccount.value }
        : undefined;
      stripeInstance.value = await loadStripe(resolvedPublishableKey.value, loadOptions);
      if (!stripeInstance.value) {
        stripeError.value = "Unable to initialize Stripe.";
        return;
      }

      stripeElements.value = stripeInstance.value.elements();
      const cardOptions: StripeCardElementOptions = {
        hidePostalCode: true,
      };
      stripeCardElement.value = stripeElements.value.create("card", cardOptions);
      stripeCardElement.value.on("change", (event: StripeCardElementChangeEvent) => {
        stripeError.value = event.error?.message || "";
        cardComplete.value = Boolean(event.complete);
      });
      stripeCardElement.value.on("ready", () => {
        stripeElementReady.value = true;
      });
      stripeReady.value = true;
    } catch (error) {
      stripeError.value = error?.message || "Payment error";
    } finally {
      stripeInitPromise.value = null;
    }
  })();

  await stripeInitPromise.value;
}

async function mountStripeCard() {
  if (!resolvedPublishableKey.value) return;
  if (stripeMountPromise.value) {
    await stripeMountPromise.value;
    return;
  }

  stripeMountPromise.value = (async () => {
    await nextTick();
    if (!stripeCardMount.value?.isConnected) return;

    await initStripeCard();

    if (!stripeCardMount.value?.isConnected || !stripeCardElement.value) return;
    if (
      stripeMounted.value &&
      mountedStripeHost.value === stripeCardMount.value
    ) {
      return;
    }

    if (stripeMounted.value) {
      stripeCardElement.value.unmount();
      stripeMounted.value = false;
    }

    stripeElementReady.value = false;
    stripeCardElement.value.mount(stripeCardMount.value);
    stripeMounted.value = true;
    mountedStripeHost.value = stripeCardMount.value;
  })();

  try {
    await stripeMountPromise.value;
  } finally {
    stripeMountPromise.value = null;
  }
}

async function waitForStripeElementReady(timeoutMs = 3000) {
  if (stripeElementReady.value) return;

  const startedAt = Date.now();

  while (!stripeElementReady.value && Date.now() - startedAt < timeoutMs) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
}

watch(
  () => ({ key: resolvedPublishableKey.value, account: resolvedStripeAccount.value }),
  async (nextConfig, previousConfig) => {
    if (
      previousConfig &&
      (previousConfig.key !== nextConfig.key ||
        previousConfig.account !== nextConfig.account)
    ) {
      destroyStripeCard();
    }

    if (!nextConfig.key) {
      destroyStripeCard();
      return;
    }

    if (!stripeCardMount.value?.isConnected) return;
    await mountStripeCard();
  },
  { immediate: false },
);

onMounted(async () => {
  await mountStripeCard();
});

onBeforeUnmount(() => {
  destroyStripeCard();
});

async function ensureReady() {
  await mountStripeCard();
  await waitForStripeElementReady();
  if (!stripeInstance.value || !stripeCardElement.value || !stripeElementReady.value) {
    stripeError.value = "Payment card is not ready yet. Please try again.";
    throw new Error(stripeError.value);
  }
}

async function validate() {
  stripeError.value = "";
  await ensureReady();
  if (!cardComplete.value) {
    stripeError.value = "Please enter your card details.";
    throw new Error(stripeError.value);
  }
}

async function confirmPayment(clientSecret: string, billingDetails: BillingDetails = {}) {
  stripeError.value = "";
  await ensureReady();

  const confirmParams = {
    payment_method: {
      card: stripeCardElement.value!,
      billing_details: billingDetails,
    },
  } as any;

  const result: PaymentIntentResult = await stripeInstance.value!.confirmCardPayment(
    clientSecret,
    confirmParams,
  );

  if (result.error) {
    stripeError.value = result.error.message || "Payment error";
    throw new Error(stripeError.value);
  }

  const pi = result.paymentIntent;
  return {
    paymentIntentId: pi?.id,
    paymentMethodId: pi?.payment_method as string | undefined,
    status: pi?.status,
    brand:
      (pi as any)?.charges?.data?.[0]?.payment_method_details?.card?.brand ||
      (pi as any)?.payment_method_options?.card?.network,
    last4: (pi as any)?.charges?.data?.[0]?.payment_method_details?.card?.last4,
  };
}

async function confirmSetup(clientSecret: string, billingDetails: BillingDetails = {}) {
  stripeError.value = "";
  await ensureReady();

  const result: SetupIntentResult = await stripeInstance.value!.confirmCardSetup(
    clientSecret,
    {
      payment_method: {
        card: stripeCardElement.value!,
        billing_details: billingDetails,
      },
    },
  );

  if (result.error) {
    stripeError.value = result.error.message || "Payment error";
    throw new Error(stripeError.value);
  }

  const si = result.setupIntent;
  return {
    setupIntentId: si?.id,
    paymentMethodId: si?.payment_method as string | undefined,
    status: si?.status,
  };
}

defineExpose({ confirmPayment, confirmSetup, validate });
</script>

<style scoped>
.payment-block {
  margin: 16px 0 10px;
  padding: 12px;
  border-radius: var(--radius-lg);
  background: #fff4ee;
  border: 1px solid #f7c9b6;
}

.payment-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-weight: 700;
  color: #1f2430;
  margin-bottom: 6px;
}

.payment-header-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.payment-header-amount {
  flex: 0 0 auto;
  text-align: right;
  white-space: nowrap;
}

.payment-help {
  margin: 0 0 10px;
  color: #6b7280;
  font-size: 13px;
}

.payment-amount-value {
  font-size: 16px;
  color: #111827;
}

.payment-missing {
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fef3c7;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
}

.payment-error {
  margin-top: 8px;
  color: #b91c1c;
  font-size: 13px;
}

.stripe-card-mount {
  min-height: 44px;
}
</style>
